import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { http, imgSrc, formatApiError } from "@/lib/api";
import { ItemFormDialog, SCHEMAS } from "@/components/admin/ItemFormDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const ItemsManager = ({ kind }) => {
  const schema = SCHEMAS[kind];
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => http.get(`/${kind}`).then((r) => setItems(r.data)), [kind]);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try {
      await http.delete(`/${kind}/${toDelete.id}`);
      toast.success(`Deleted ${toDelete.title}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setToDelete(null);
  };

  const publicPath = { properties: "/realty", projects: "/development", shortlets: "/shortlets" }[kind];

  return (
    <div data-testid={`admin-${kind}-manager`}>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <p className="text-gw-ground/80">{items ? `${items.length} ${kind}` : "Loading..."}</p>
        <button type="button" onClick={() => { setEditing(null); setOpen(true); }} data-testid={`admin-add-${kind}-button`}
          className="lab inline-flex items-center gap-2 bg-gw-ground text-white px-5 py-3 transition-colors duration-200 hover:bg-gw-deep">
          <Plus size={14} /> Add {schema.label}
        </button>
      </div>
      <div className="border-t border-gw-ground/20">
        {(items || []).map((it, i) => (
          <div key={it.id} className="flex items-center gap-4 py-4 border-b border-gw-ground/20" data-testid={`admin-${kind}-row-${i}`}>
            <div className="w-20 h-14 bg-gw-card overflow-hidden shrink-0">
              {it.images?.[0] && <img src={imgSrc(it.images[0])} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="lux text-xl truncate">{it.title}</p>
              <p className="text-sm text-gw-ground/70 truncate">
                {[it.listing_type || it.status, it.location, it.price, it.nightly_rate ? `₦${Number(it.nightly_rate).toLocaleString("en")}/night` : null].filter(Boolean).join(" · ")}
                {it.is_sample && <span className="lab ml-2 bg-gw-tint px-1.5 py-0.5 text-[10px]">Sample</span>}
              </p>
            </div>
            <Link to={`${publicPath}/${it.id}`} target="_blank" aria-label="View on site" className="p-2 hover:bg-gw-tint transition-colors duration-200" data-testid={`admin-${kind}-view-${i}`}><ExternalLink size={16} /></Link>
            <button type="button" aria-label="Edit" onClick={() => { setEditing(it); setOpen(true); }} className="p-2 hover:bg-gw-tint transition-colors duration-200" data-testid={`admin-${kind}-edit-${i}`}><Pencil size={16} /></button>
            <button type="button" aria-label="Delete" onClick={() => setToDelete(it)} className="p-2 text-destructive hover:bg-red-50 transition-colors duration-200" data-testid={`admin-${kind}-delete-${i}`}><Trash2 size={16} /></button>
          </div>
        ))}
        {items?.length === 0 && <p className="py-10 text-gw-ground/70">Nothing yet — add your first {schema.label}.</p>}
      </div>

      <ItemFormDialog kind={kind} item={editing} open={open} onOpenChange={setOpen} onSaved={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-none bg-white text-gw-deep">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{toDelete?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>It will be removed from the public site.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="admin-delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="rounded-none bg-destructive text-white hover:bg-destructive/90" data-testid="admin-delete-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
